import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlus, faTruck, faEye, faCheck } from '@fortawesome/free-solid-svg-icons';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { app } from '../firebase';
import swal from "sweetalert";
import {
  getFirestore,
  collection,
  orderBy,
  query,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import axios from "axios";

function TripComp() {
  const db = getFirestore(app);
  const [trips, setTrips] = useState([]);
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [tripName, setTripName] = useState('');
  const [activeTrip, setActiveTrip] = useState(null);
  const [allOrders, setAllOrders] = useState([]); // store all orders for trip view

  // Fetch trips
  const fetchTrips = async () => {
    const q = query(collection(db, 'Trips'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setTrips(data);
  };

  useEffect(() => {
    fetchTrips();
  }, [db]);

  // Fetch orders (only picked for trip creation)
  useEffect(() => {
    const fetchOrders = async () => {
      const q = query(collection(db, 'Orders'), orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(order => order.status === 'Pending');
      setOrders(data);
      setAllOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); // keep all orders for trip view
    };

    fetchOrders();
  }, [db]);

  // Fetch drivers 
  useEffect(() => {
    const fetchDrivers = async () => {
      const q = query(collection(db, 'drivers'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDrivers(data);
    };

    fetchDrivers();
  }, [db]);

  const handleOrderSelect = (id) => {
    setSelectedOrders(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleCreateTrip = async () => {
    if (!tripName || !selectedDriver || selectedOrders.length === 0) {
      alert('Please fill in all fields and select at least one order.');
      return;
    }

    try {
      // Create trip document
      const tripDoc = await addDoc(collection(db, 'Trips'), {
        tripName,
        driverId: selectedDriver,
        orderIds: selectedOrders,
        status: 'Ongoing',
        createdAt: Timestamp.now(),
      });

      // Update all selected orders to Ongoing
      for (let orderId of selectedOrders) {
        const orderRef = doc(db, 'Orders', orderId);
        await updateDoc(orderRef, { status: 'Ongoing', tripId: tripDoc.id });
      }

      // Reset form
      setTripName('');
      setSelectedDriver('');
      setSelectedOrders([]);
      setShowModal(false);

      // Refresh trips
      fetchTrips();
    } catch (err) {
      console.error('Error creating trip:', err);
    }
  };

  const handleViewTrip = (trip) => {
    setActiveTrip(trip);
    setShowViewModal(true);
  };

  const getDriverName = (driverId) => {
    return drivers.find(d => d.id === driverId)?.full_name || '';
  };

  const getOrderClientName = (order) => {
    return order?.clientName || order?.newClientName || order?.fromContact || '';
  };

  const getTripClientNames = (trip) => {
    const names = trip.orderIds
      ?.map(orderId => allOrders.find(o => o.id === orderId))
      .map(order => getOrderClientName(order))
      .filter(Boolean) || [];
    const unique = Array.from(new Set(names));
    return unique.join(', ');
  };

  const getSmsGreeting = (name) => {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    return trimmed ? `Hi ${trimmed},` : 'Dear client,';
  };

  const normalizePhone = (phone) =>
    typeof phone === 'string' ? phone.trim() : '';

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredTrips = trips.filter((trip) => {
    if (!normalizedSearch) return true;
    const values = [
      trip.tripName,
      getDriverName(trip.driverId),
      getTripClientNames(trip),
      trip.status,
      trip.createdAt?.toDate().toLocaleString(),
      trip.id
    ];
    return values.some((value) =>
      String(value || '').toLowerCase().includes(normalizedSearch)
    );
  });


  // Start Journey function
// Start Journey function
const handleStartJourney = (trip) => {
  swal({
    title: "Start Journey?",
    text: "Do you want to mark this trip and all its orders as 'On Journey'?",
    icon: "info",
    buttons: ["Cancel", "Yes, Start"],
  }).then(async (willStart) => {
    if (willStart) {
      try {
        const tripRef = doc(db, 'Trips', trip.id);
        await updateDoc(tripRef, { status: 'onJourney' });

        // Get driver details
        const driver = drivers.find(d => d.id === trip.driverId);
        const driverName = driver?.full_name || "Driver";
        const driverPhone = driver?.userphone || "N/A";

        // Update all related orders and send SMS
        for (let orderId of trip.orderIds) {
          const order = allOrders.find(o => o.id === orderId);
          if (!order) continue;

          const orderRef = doc(db, 'Orders', orderId);
          await updateDoc(orderRef, { status: 'onJourney' });

          const trackingLink = `https://dropoff-mtaani.web.app/track-order/${orderId}`;

          // SMS for the person who placed the order
          const clientName = order.newClientName || order.fromContact;
          const clientGreeting = getSmsGreeting(clientName);
          const clientMessage = `${clientGreeting} your delivery to ${order.toLocation} has started. Driver: ${driverName}, Phone: ${driverPhone}. Track here: ${trackingLink}`;

          // SMS for the recipient
          const recipientMessage = `Hi, a delivery placed to your location (${order.toLocation}) is on the way. Driver: ${driverName}, Phone: ${driverPhone}. Track here: ${trackingLink}`;

          try {
            // Send to client
            if (order.clientPhone) {
              await axios.post(
                "https://us-central1-dropoff-mtaani.cloudfunctions.net/api/send-sms",
                {
                  message: clientMessage,
                  mobile: order.clientPhone,
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                  },
                }
              );
            }

            // Send to recipient
            if (order.toContact) {
              await axios.post(
                "https://us-central1-dropoff-mtaani.cloudfunctions.net/api/send-sms",
                {
                  message: recipientMessage,
                  mobile: order.toContact,
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                  },
                }
              );
            }
          } catch (smsErr) {
            console.error(`Failed to send SMS for order ${orderId}:`, smsErr);
          }
        }

        fetchTrips();
        swal("Success", "Trip and all its orders are now 'On Journey'. SMS sent to clients and recipients.", "success");
      } catch (err) {
        console.error('Error starting journey:', err);
        swal("Error", "Failed to start journey.", "error");
      }
    }
  });
};



  // Complete Trip function
// Complete Trip function
const handleCompleteTrip = (trip) => {
  swal({
    title: "Complete Trip?",
    text: "Do you want to mark this trip and its orders as completed?",
    icon: "warning",
    buttons: ["Cancel", "Yes, Complete"],
    dangerMode: true,
  }).then(async (willComplete) => {
    if (willComplete) {
      try {
        const tripRef = doc(db, 'Trips', trip.id);
        await updateDoc(tripRef, { status: 'Completed' });

        // Update all related orders and send SMS
        for (let orderId of trip.orderIds) {
          const order = allOrders.find(o => o.id === orderId);
          if (!order || order.status === 'Completed') continue;

          const orderRef = doc(db, 'Orders', orderId);
          await updateDoc(orderRef, { 
            status: 'Completed', 
            paymentStatus: 'Unpaid' 
          });

          // Messages
          const clientName = order.newClientName || order.fromContact;
          const clientGreeting = getSmsGreeting(clientName);
          const requesterPhone = normalizePhone(order.clientPhone);
          const pickupPhone = normalizePhone(order.fromContactPhone);
          const clientMessage = `${clientGreeting} your order to ${order.toLocation} has been completed.`;
          const pickupMessage = `Hi, the order from ${order.fromLocation} to ${order.toLocation} has been completed.`;

          try {
            // Send to the requester
            if (requesterPhone) {
              await axios.post(
                "https://us-central1-dropoff-mtaani.cloudfunctions.net/api/send-sms",
                {
                  message: clientMessage,
                  mobile: requesterPhone,
                },
                {
                  headers: { "Content-Type": "application/json" },
                }
              );
            }

            // Send to the pickup-side contact
            if (pickupPhone && pickupPhone !== requesterPhone) {
              await axios.post(
                "https://us-central1-dropoff-mtaani.cloudfunctions.net/api/send-sms",
                {
                  message: pickupMessage,
                  mobile: pickupPhone,
                },
                {
                  headers: { "Content-Type": "application/json" },
                }
              );
            }
          } catch (smsErr) {
            console.error(`Failed to send SMS for order ${orderId}:`, smsErr);
          }
        }

        fetchTrips();
        swal("Success", "Trip and its orders marked as completed. SMS sent to the requester and pickup contact.", "success");
      } catch (err) {
        console.error('Error completing trip:', err);
        swal("Error", "Failed to complete trip.", "error");
      }
    }
  });
};

  return (
    <div className="dataComp p-4">
      <div className="dataCompTop d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-bold">All Trips</h5>
        <div className="searchContainer d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faSearch} className="searchIcon text-muted" />
          <input
            type="text"
            placeholder="Type to search"
            className="form-control"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button variant="success" onClick={() => setShowModal(true)}>
            <FontAwesomeIcon icon={faPlus} className="me-2" /> Create Trip
          </Button>
        </div>
      </div>

      {/* Trips Table */}
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Trip Name</th>
            <th>Driver</th>
            <th>Clients</th>
            <th>Orders</th>
            <th>Status</th>
            <th>Created</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {filteredTrips.map(trip => (
            <tr key={trip.id}>
              <td>{trip.tripName}</td>
              <td>{getDriverName(trip.driverId) || 'N/A'}</td>
              <td>{getTripClientNames(trip) || 'N/A'}</td>
              <td>{trip.orderIds?.length || 0}</td>
              <td>
                <span
                  className={`badge ${
                    trip.status === 'Ongoing'
                      ? 'bg-primary'
                      : trip.status === 'onJourney'
                        ? 'bg-warning text-dark'
                        : trip.status === 'Completed'
                          ? 'bg-success'
                          : 'bg-secondary'
                  }`}
                >
                  {trip.status}
                </span>
              </td>
              <td>{trip.createdAt?.toDate().toLocaleString()}</td>
              <td>
                <Button size="sm" variant="info" onClick={() => handleViewTrip(trip)}>
                  <FontAwesomeIcon icon={faEye} className="me-1" /> View
                </Button>

                {trip.status === 'Ongoing' && (
                  <Button
                    size="sm"
                    variant="warning"
                    className="ms-1"
                    onClick={() => handleStartJourney(trip)}
                  >
                    <FontAwesomeIcon icon={faTruck} className="me-1" /> Start Journey
                  </Button>
                )}

                {trip.status !== 'Completed' && (
                  <Button
                    size="sm"
                    variant="success"
                    className="ms-1"
                    onClick={() => handleCompleteTrip(trip)}
                  >
                    <FontAwesomeIcon icon={faCheck} className="me-1" /> Complete
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Create Trip Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create a New Trip</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Trip Name</Form.Label>
              <Form.Control
                type="text"
                value={tripName}
                onChange={(e) => setTripName(e.target.value)}
                placeholder="E.g., Ngong Road Consolidation"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Select Driver</Form.Label>
              <Form.Select
                value={selectedDriver}
                onChange={(e) => setSelectedDriver(e.target.value)}
              >
                <option value="">-- Select Driver --</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.full_name} ({driver.userphone})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label>Select Orders</Form.Label>
              <div
                style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '10px',
                }}
              >
                {orders.map(order => (
                  <Form.Check
                    key={order.id}
                    type="checkbox"
                    id={`order-${order.id}`}
                    label={`${order.fromLocation} ➝ ${order.toLocation} — ${getOrderClientName(order) || 'N/A'}`}
                    checked={selectedOrders.includes(order.id)}
                    onChange={() => handleOrderSelect(order.id)}
                  />
                ))}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateTrip}>
            <FontAwesomeIcon icon={faTruck} className="me-2" />
            Create Trip
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Trip Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Trip Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeTrip && (
            <Row>
              {/* Orders */}
              <Col md={8}>
                <h6 className="mb-3">Orders in this Trip</h6>
                <Table bordered hover responsive>
                  <thead className="table-light">
                    <tr>
                      <th>From</th>
                      <th>To</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeTrip.orderIds?.map(orderId => {
                      const order = allOrders.find(o => o.id === orderId);
                      return (
                        <tr key={orderId}>
                          <td>{order?.fromLocation || 'N/A'}</td>
                          <td>{order?.toLocation || 'N/A'}</td>
                          <td>{order?.status || 'N/A'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Col>

              {/* Driver */}
              <Col md={4}>
                <h6 className="mb-3">Assigned Driver</h6>
                <Card className="shadow-sm">
                  <Card.Body>
                    {(() => {
                      const driver = drivers.find(d => d.id === activeTrip.driverId);
                      return driver ? (
                        <>
                          <p><strong>Name:</strong> {driver.full_name}</p>
                          <p><strong>Phone:</strong> {driver.userphone}</p>
                          <p><strong>Vehicle:</strong> {driver.vehicleType || 'N/A'}</p>
                        </>
                      ) : (
                        <p>No driver found</p>
                      );
                    })()}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default TripComp;
